// Utilidad para calcular promedio de un array de números
export const calculateAverage = (numbers: number[]): number => {
  if (numbers.length === 0) return 0;
  return numbers.reduce((a: number, b: number) => a + b, 0) / numbers.length;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const fullYear = date.getFullYear();
  const monthNumber = date.getMonth() + 1; // 0-based
  const dayOfMonth = date.getDate();
  const month = String(monthNumber).padStart(2, '0');
  const day = String(dayOfMonth).padStart(2, '0');
  return `${fullYear}-${month}-${day}`;
};

/**
 * Calcula la fecha mínima desde hoy restando una cantidad de meses
 * Utilizado para limitar el rango de fechas en selectores de asistencia y ausencias
 * @param months - Cantidad de meses a restar desde hoy (por defecto 60)
 * @returns Fecha en formato YYYY-MM-DD
 */
export const getMinDateByMonths = (months: number = 60): string => {
  const minDate = new Date();
  minDate.setMonth(minDate.getMonth() - (months - 1)); // months - 1 para incluir el mes actual
  return formatDateToYYYYMMDD(minDate);
};

// Convert time value to HH:mm:ss format
export const toHHmmss = (value?: string): string => {
  if (!value) return '';
  const hhMmMatch = value.match(/^(\d{2}):(\d{2})$/);
  if (hhMmMatch) return `${value}:00`;
  const match = value.match(/^(\d{2}):(\d{2}):(\d{2})(?:\s?[APap][Mm])?$/);
  if (match) {
    const [, hh, mm, ss] = match;
    return `${hh}:${mm}:${ss}`;
  }
  const dt = new Date(value);
  if (!isNaN(dt.getTime())) {
    const hh = dt.getHours().toString().padStart(2, '0');
    const mm = dt.getMinutes().toString().padStart(2, '0');
    const ss = dt.getSeconds().toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  return value;
};

// Capitalize only the very first character of the string (if alphabetic) and lower-case the rest
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length === 0) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

// Capital case (Title Case style that capitalizes every word)
// Converts the first character of every word to uppercase, lowercases the rest.
// Collapses multiple spaces.
// Example: "  juAn   péRez  gOnZález" -> "Juan Pérez González"
export const toCapitalCase = (text: string): string => {
  if (!text) return '';
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Formatea el nombre de una persona para presentación al usuario.
//
// El ORDEN del nombre ("ApellidoPaterno ApellidoMaterno Nombres") es
// responsabilidad del backend: cada query lo construye y lo ordena en ese
// orden, de modo que el ORDER BY, la paginación y la búsqueda coincidan con lo
// que se muestra. Aquí sólo se normaliza el caso, porque UserExtraData guarda
// los nombres sin normalizar (mayoritariamente en MAYÚSCULAS, pero no siempre).
//
// Un sufijo entre paréntesis al final (habitualmente el RUT) se preserva sin
// alterar su caso, para no convertir "12345678-K" en "12345678-k".
export const formatPersonName = (name: string): string => {
  if (!name) return '';

  const parentheticalMatch = name.match(/^(.*?)(\s*\([^()]*\))\s*$/);
  const namePart = parentheticalMatch ? parentheticalMatch[1] : name;
  const suffix = parentheticalMatch ? parentheticalMatch[2].trim() : '';

  const formatted = toCapitalCase(namePart);

  if (!formatted) return suffix;
  return suffix ? `${formatted} ${suffix}` : formatted;
};

// Comparador genérico usado por Table
export function tableValueCompare(valueA: any, valueB: any, direction: 'asc' | 'desc'): number {
  if (valueA == null) return 1;
  if (valueB == null) return -1;
  if (valueA === valueB) return 0;
  if (valueA > valueB) return direction === 'asc' ? 1 : -1;
  return direction === 'asc' ? -1 : 1; 
}

// Format date to dd/mm/yyyy
export const formatToDdMmYyyy = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  if (typeof date === 'string') {
    // Handle dd/mm/yyyy (with optional time)
    const ddmmyyyyMatch = date.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (ddmmyyyyMatch) {
      const [, d, m, y] = ddmmyyyyMatch;
      // Validate date
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return ''; // Invalid month
      if (day < 1 || day > 31) return ''; // Simple day validation
        
      return `${d}/${m}/${y}`;
    }

    // Handle dd-mm-yyyy (with optional time)
    const ddmmyyyyDashMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (ddmmyyyyDashMatch) {
      const [, d, m, y] = ddmmyyyyDashMatch;
      // Validate
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return '';
      if (day < 1 || day > 31) return '';

      return `${d}/${m}/${y}`;
    }
    
    // Handle yyyy-mm-dd (with optional time)
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      // No validation for ISO format to match legacy behavior/tests
      return `${d}/${m}/${y}`;
    }

    // Handle YYYYMMDD
    const compactMatch = date.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compactMatch) {
      const [, y, m, d] = compactMatch;
      // Validate
      const day = parseInt(d, 10);
      const month = parseInt(m, 10);
        
      if (month < 1 || month > 12) return '';
      if (day < 1 || day > 31) return '';

      return `${d}/${m}/${y}`;
    }
  }
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
};

// Inicial del día de la semana en español para una fecha dd/mm/yyyy (ASIS-5572, columna Jornada de
// Gestor de Marcas). Miércoles usa "X" (no "M", ya reservada para martes) — catálogo cerrado de 7
// letras acordado con negocio. Parseo manual (no `new Date(dateString)`) para evitar ambigüedad de
// interpretación de formato entre navegadores/locales.
const WEEKDAY_INITIALS: readonly string[] = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // índice = Date#getDay()

// Parseo estricto compartido por getWeekdayInitial/addDaysToDdMmYyyy — null si el string no calza
// con dd/mm/yyyy o no representa una fecha válida.
const parseDdMmYyyy = (dateDdMmYyyy: string): Date | null => {
  const match = dateDdMmYyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const [, d, m, y] = match;
  const parsed = new Date(Number(y), Number(m) - 1, Number(d));
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const getWeekdayInitial = (dateDdMmYyyy: string): string => {
  const parsed = parseDdMmYyyy(dateDdMmYyyy);
  return parsed ? WEEKDAY_INITIALS[parsed.getDay()] : '';
};

// Suma (o resta, con `days` negativo) días a una fecha dd/mm/yyyy, preservando el mismo formato de
// salida (ASIS-5572: calcula la fecha del día anterior/siguiente al agregar una jornada adicional
// en Gestor de Marcas).
export const addDaysToDdMmYyyy = (dateDdMmYyyy: string, days: number): string => {
  const parsed = parseDdMmYyyy(dateDdMmYyyy);
  if (!parsed) return dateDdMmYyyy;

  parsed.setDate(parsed.getDate() + days);

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatToDdMmYyyyHHmm = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateValue = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateValue.getTime())) return '';

  const day = String(dateValue.getDate()).padStart(2, '0');
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const year = dateValue.getFullYear();
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const formatToDdMmYyyyHHmmss = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateValue = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateValue.getTime())) return '';

  const day = String(dateValue.getDate()).padStart(2, '0');
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const year = dateValue.getFullYear();
  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
  const seconds = String(dateValue.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};
